import axios from "@/inside/axios";
import Text from "@/inside/models/Text/Text";
import TextType from "@/inside/models/Text/Type";
import Thematic from "@/inside/models/Text/Thematic";
import Operator from "@/inside/models/Filter/Operator";

export default {
    actions: {
        /**
         * Loads initial data. Request to Echoline API is only sent once.
         * @param {object} context
         * @return {Promise}
         */
        init({state, rootGetters}) {
            return new Promise((resolve, reject) => {
                axios.get("/my/texts", {
                    validateStatus: status => status === 200
                }).then((response) => {
                    const types = {};
                    const texts = [];

                    response.data.forEach((text_data) => {
                        const thematics = text_data.thematics.map((thematic_data) => {
                            return rootGetters["thematics/one"](thematic_data.id);
                        });

                        if (!Object.prototype.hasOwnProperty.call(types, text_data.type.id)) {
                            types[text_data.type.id] = Object.freeze(TextType.fromAPI(text_data.type));
                        }

                        let text = Text.fromAPI(text_data);
                        text.type = types[text_data.type.id];
                        text.thematics = thematics;
                        texts.push(Object.freeze(text));
                    });

                    state.texts = texts;
                    resolve();
                }).catch((error) => {
                    reject(error);
                });
            });
        },
        aiSearch({state, rootGetters}, searchInput) {
            return new Promise((resolve, reject) => {
                axios.get("/texts/ai-search", {params: {query: searchInput}})
                    .then((response) => {

                        const types = {};
                        const texts = [];

                        response.data.forEach((text_data) => {
                            const thematics = text_data.thematics.map((thematic_data) => {
                                return rootGetters["thematics/one"](thematic_data.id);
                            });

                            if (!Object.prototype.hasOwnProperty.call(types, text_data.type.id)) {
                                types[text_data.type.id] = Object.freeze(TextType.fromAPI(text_data.type));
                            }

                            let text = Text.fromAPI(text_data);
                            text.type = types[text_data.type.id];
                            text.thematics = thematics;
                            texts.push(Object.freeze(text));
                        });

                        state.texts = texts;
                        resolve();
                    })
                    .catch((error) => {
                        console.log(error);
                        reject(error);
                    })
                ;
            })
        }
    },
    getters: {
        /**
         * Returns all texts, not filtered.
         * @param {object} state
         * @return {Text[]}
         */
        all(state) {
            return state.texts;
        },
        /**
         * Returns current search query.
         * @param {object} state
         * @return {string}
         */
        currentSearchQuery(state) {
            return state.currentSearchQuery;
        },
        /**
         * Returns filtered texts as an array of Text instances.
         * @param {object} state
         * @param getters
         * @param rootState
         * @param rootGetters
         * @return {Text[]}
         */
        filtered(state, getters, rootState, rootGetters) {
            const assignmentsByText = rootGetters["assignments/byText"];

            return Array.from(state.texts).filter((text) => {
                if (!Object.prototype.hasOwnProperty.call(assignmentsByText, text.internalID)) {
                    return false;
                }

                let filter = rootGetters["filters/one"]("watch.texttypes.id");

                if (
                    filter
                    && filter.value.includes(text.type.id)
                ) {
                    return false;
                }

                filter = rootGetters["filters/one"]("search.watch");

                if (filter) {
                    const regexp = (filter.value.trim() !== '') ? new RegExp(filter.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i") : null;

                    if (
                        regexp !== null
                        && !regexp.test(text.label)
                        && !regexp.test(text.reference)
                    ) {
                        return false;
                    }
                }

                filter = rootGetters["filters/one"]("watch.status.text");

                if (filter) {
                    if (
                        filter.value === "new"
                        && !text.highlight.new
                    ) {
                        return false;
                    }

                    if (
                        filter.value === "amended"
                        && !text.highlight.amended
                    ) {
                        return false;
                    }

                    if (
                        filter.value === "both"
                        && (
                            !text.highlight.new
                            && !text.highlight.amended
                        )
                    ) {
                        return false;
                    }
                }

                filter = rootGetters["filters/one"]("watch.status.local");

                if(filter){
                    if(
                        filter.value === true
                        && !text.isLocal
                    ){
                        return false;
                    }

                    if(
                        filter.value === false
                        && text.isLocal
                    ){
                        return false;
                    }
                }

                filter = rootGetters["filters/one"]("watch.categories.id");

                if (
                    filter
                    && filter.value.includes(text.thematics[0].id)
                ) {
                    return false;
                }

                filter = rootGetters["filters/one"]("watch.textdates.signature");

                if (filter) {
                    if (text.date.signature === null) {
                        return false;
                    }

                    const date = text.date.signature.valueOf();

                    if (
                        date < filter.value[0].getTime()
                        || date > filter.value[1].getTime()
                    ) {
                        return false;
                    }
                }

                filter = rootGetters["filters/one"]("watch.textdates.amendment");

                if (filter) {
                    if (text.lastAmendment === null) {
                        return false;
                    }

                    if (text.lastAmendment.date.signature === null) {
                        // Should not be removed, as it's a meaningful info
                        console.warn(`Text ${text.internalID} was filtered due to missing signature date`);
                        return false;
                    }

                    const date = text.lastAmendment.date.signature.valueOf();

                    if (
                        date < filter.value[0].getTime()
                        || date > filter.value[1].getTime()
                    ) {
                        return false
                    }
                }
                const assignments = assignmentsByText[text.internalID];
                const textIsForcedInformational = assignments.find((assignment) => {
                    return !assignment.isInformational;
                }) === undefined;

                filter = rootGetters["filters/one"]("watch.status.assessment");

                if (filter) {
                    if (
                        !text.isInformational
                        && !textIsForcedInformational
                    ) {

                        let numerator = 0;
                        let denominator = 0;

                        assignments.forEach((assignment) => {
                            numerator += assignment.stats.assessed;
                            denominator += assignment.stats.nbAssessableRequirements
                        });

                        const rate = denominator === 0 ? 0 : Math.floor((numerator * 100) / denominator);

                        if (
                            rate < filter.value[0]
                            || rate > filter.value[1]
                        ) {
                            return false
                        }
                    }
                }

                filter = rootGetters["filters/one"]("watch.status.compliance");

                if (filter) {
                    if (
                        !text.isInformational
                        && !textIsForcedInformational
                    ) {
                        let numerator = 0;
                        let denominator = 0;

                        assignments.forEach((assignment) => {
                            numerator += assignment.stats["compliance.yes"];
                            denominator += assignment.stats["relevance.yes"]
                        });

                        const rate = denominator === 0 ? 0 : Math.floor((numerator * 100) / denominator);

                        if (
                            rate < filter.value[0]
                            || rate > filter.value[1]
                        ) {
                            return false
                        }
                    }
                }

                return true;
            });
        },
        /**
         * Returns texts filtered using new filter model.
         * @return {Array<Readonly<Text>>}
         */
        filteredNG(state, getters, rootState, rootGetters) {
            /**
             * @type {Store}
             */
            const filters = rootGetters["ngFilters/store"];
            const assignmentsByText = rootGetters["assignments/byTextNG"];

            return rootGetters["texts/all"].filter((text) => {
                if (!Object.prototype.hasOwnProperty.call(assignmentsByText, text.id)) {
                    return false;
                }

                /* Filtering by text types */

                if (filters.textTypes.isExcluded(text.type.id)) {
                    return false;
                }

                /* Filtering by text events */

                if (
                    filters.textEvent.value === "recently-added"
                    && !text.highlight.new
                ) {
                    return false;
                }

                if (
                    filters.textEvent.value === "recently-amended"
                    && !text.highlight.amended
                ) {
                    return false;
                }

                if (
                    filters.textEvent.value === "both"
                    && (
                        !text.highlight.new
                        && !text.highlight.amended
                    )
                ) {
                    return false;
                }

                /* Filtering on local texts */

                if (
                    filters.textLocalStatus.value === "1"
                    && !text.isLocal
                ) {
                    return false;
                }

                if (
                    filters.textLocalStatus.value === "0"
                    && text.isLocal
                ) {
                    return false;
                }

                /* Filtering by thematics */

                if (filters.thematics.isExcluded(text.thematics[0].id)) {
                    return false;
                }

                /* Filtering by rates */

                const assignments = assignmentsByText[text.id];
                const areAllAssignmentsInformational = assignments.find((assignment) => {
                    return !assignment.isInformational;
                }) === undefined;

                if ((
                    filters.textAssessmentRate.start !== 0
                    || filters.textAssessmentRate.end !== 100
                ) && (
                    !text.isInformational
                    && !areAllAssignmentsInformational
                )) {
                    let numerator = 0;
                    let denominator = 0;

                    assignments.forEach((assignment) => {
                        numerator += assignment.stats.assessed;
                        denominator += assignment.stats.nbAssessableRequirements
                    });

                    const rate = denominator === 0 ? 0 : Math.floor((numerator * 100) / denominator);

                    if (
                        rate < filters.textAssessmentRate.start
                        || rate > filters.textAssessmentRate.end
                    ) {
                        return false;
                    }
                }

                if ((
                    filters.textComplianceRate.start !== 0
                    || filters.textComplianceRate.end !== 100
                ) && (
                    !text.isInformational
                    && !areAllAssignmentsInformational
                )) {
                    let numerator = 0;
                    let denominator = 0;

                    assignments.forEach((assignment) => {
                        numerator += assignment.stats["compliance.yes"];
                        denominator += assignment.stats["relevance.yes"]
                    });

                    const rate = denominator === 0 ? 0 : Math.floor((numerator * 100) / denominator);

                    if (
                        rate < filters.textComplianceRate.start
                        || rate > filters.textComplianceRate.end
                    ) {
                        return false;
                    }
                }

                /* Filtering by dates */

                if (
                    !filters.textSignatureDateRange.isUnbound()
                    && text.date.signature === null
                ) {
                    return false;
                }

                if (
                    !filters.textLastAmendmentDateRange.isUnbound()
                    && (
                        text.lastAmendment === null
                        || text.lastAmendment.date.signature === null
                    )
                ) {
                    return false;
                }

                const signatureDateIncluded = (
                    filters.textSignatureDateRange.isUnbound()
                    || filters.textSignatureDateRange.isIncluded(text.date.signature)
                );

                const lastAmendmentDateIncluded = (
                    filters.textLastAmendmentDateRange.isUnbound()
                    || filters.textLastAmendmentDateRange.isIncluded(text.lastAmendment.date.signature)
                );

                if (filters.textDatesOperator.value === Operator.and) {
                    if (!(
                        signatureDateIncluded
                        && lastAmendmentDateIncluded
                    )) {
                        return false;
                    }
                } else { // filters.textDatesOperator.value === Operator.or
                    if (!(
                        signatureDateIncluded
                        || lastAmendmentDateIncluded
                    )) {
                        return false;
                    }
                }

                /* Filtering with last search results */

                // noinspection RedundantIfStatementJS
                if (filters.textSearch.isExcluded(text.id)) {
                    return false;
                }

                return true;
            });
        },
        /**
         * Returns any `Text` instance in module state corresponding to given `text_internal_id`.
         * @param {object} state
         * @return {function(*): Text|undefined}
         */
        one(state) {
            return (text_internal_id) => {
                return state.texts.find((text) => {
                    return parseInt(text.internalID) === parseInt(text_internal_id);
                });
            };
        },
        /**
         * Returns any `Text` instance in module state corresponding to given `text_id`.
         * @param {object} state
         * @return {function(string): Text|undefined}
         */
        oneByID(state) {
            return (text_id) => {
                return state.texts.find((text) => {
                    return text.id === text_id;
                });
            };
        },
        /**
         * Returns last score order.
         * @param {object} state
         * @return {string[]}
         */
        scoreOrder(state) {
            return state.scoreOrder;
        },
        /**
         * Returns current sorting order.
         * @param {object} state
         * @return {string}
         */
        sort(state) {
            return state.sort;
        },
        /**
         * Returns all thematics currently referenced by texts, including parent thematics.
         * @param {object} state
         * @return {Thematic[]}
         */
        thematics(state) {
            const out = {};

            state.texts.forEach((text) => {
                text.thematics.forEach((thematic) => {
                    let cursor = thematic;

                    while (cursor instanceof Thematic) {
                        if (!Object.prototype.hasOwnProperty.call(out, cursor.id)) {
                            out[cursor.id] = cursor;
                        }

                        cursor = cursor.parent;
                    }
                });
            });

            return Object.values(out);
        },
        /**
         * Returns all text types, sorted by alphabetical order.
         * @param {object} state
         * @return {Type[]}
         */
        types(state) {
            let out = {};

            state.texts.forEach((text) => {
                if (!Object.prototype.hasOwnProperty.call(out, text.type.id)) {
                    out[text.type.id] = text.type;
                }
            });

            return Object.values(out).sort((typeA, typeB) => {
                return typeA.label.localeCompare(typeB.label);
            });
        }
    },
    mutations: {
        /**
         * Stores given `texts` in module state.
         * @param {object} state
         * @param {Text[]} texts
         */
        set(state, texts) {
            if (!Array.isArray(texts)) {
                throw new TypeError(`VueX 'texts.set' mutation expects payload to be an array, ${typeof texts} given`);
            }

            state.texts = texts;
        },
        /**
         * Stores current search query.
         * @param {object} state
         * @param {string} query
         */
        setCurrentSearchQuery(state, query) {
            state.currentSearchQuery = query;
        },
        /**
         * Stores given `sort` as current sorting order.
         * @param {object} state
         * @param {string} sort
         */
        setSort(state, sort) {
            state.sort = sort;
        },
        /**
         * Stores given `order` as current score order.
         * @param {object} state
         * @param {string[]} order
         */
        setScoreOrder(state, order) {
            state.scoreOrder = order;
        }
    },
    namespaced: true,
    state: {
        currentSearchQuery: "",
        scoreOrder: [],
        sort: "",
        texts: []
    }
}
